import { useEffect, useId, useRef, useState } from "react";
import { zonedToUtc } from "../../src/zoned.ts";
import { ApiError, api, type Reminder, type TripView } from "./api.ts";
import { Modal } from "./Dialog.tsx";
import { listNames, localWhen, sentence, weekday, zoneCity } from "./format.ts";
import { asLabel } from "./former.tsx";
import { useGrow } from "./hooks.ts";
import { useJustDone } from "./justDone.ts";
import { useKept, useOnline } from "./offline.ts";
import { ReportThis } from "./ReportThis.tsx";
import { useReadOnly } from "./readOnly.ts";
import { Button, Icon, SegmentedControl } from "./souvenir.ts";
import { factLine } from "./ui.tsx";

// Reminders on the web (DESIGN.md, Views, the web's own edits; the log's "Reminders on the web";
// features/tasks-and-reminders.md): each reminder still to come on Bookings' Reminders is a row that opens it in a
// dialog, in full: its whole message, when in its own time zone, who it's for, what it's about (that card a tap away)
// and who set it, when. Anyone in the trip changes its words, its day and time (in its own time zone, which stays as
// set) and who it's for, or removes it, as through an AI: one write each, signed "on the web", undoable at once (the
// trip's notice) and from Updates, over the reminder as the dialog read it. On a read-only trip it's only read.

const MAX_TEXT = 500;

// Who it's for, from the reader's side: "Everyone in the trip", "You and Mei".
function whom(r: Reminder, trip: TripView): string {
  if (r.to === "everyone") return "Everyone in the trip";
  return sentence(listNames(r.to.map((p) => (p.id === trip.you ? "you" : asLabel(p.name)))));
}

// Its row's line under its words: "Wed 18 Nov, 09:00 Singapore time · to Ben · Buy JR passes".
function rowFacts(r: Reminder, trip: TripView) {
  return factLine([
    localWhen(r.at, r.tz),
    r.to === "everyone"
      ? "to everyone"
      : `to ${r.to.map((p) => (p.id === trip.you ? "you" : asLabel(p.name))).join(", ")}`,
    r.about?.title,
  ]);
}

// A reminder on Bookings: a button opening it, its words wrapping onto as many lines as they need.
export function ReminderRow({
  reminder,
  trip,
  onOpen,
}: {
  reminder: Reminder;
  trip: TripView;
  onOpen: (id: string) => void;
}) {
  return (
    <li>
      <button
        type="button"
        className="tm-row tm-reminder-row"
        aria-haspopup="dialog"
        onClick={() => onOpen(reminder.id)}
      >
        <span className="tm-row-icon">
          <Icon name="bell" size={18} />
        </span>
        <span className="tm-row-text">
          <span className="callout">{reminder.text}</span>
          <span className="caption tm-muted">{rowFacts(reminder, trip)}</span>
        </span>
      </button>
    </li>
  );
}

// The day it was set, on this device's calendar: "Sun 1 Nov".
function setOn(at: string): string {
  const d = new Date(at);
  const pad = (n: number) => String(n).padStart(2, "0");
  return weekday(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
}

// The reminder, opened: read, changed or removed. `reminder` is it as the page has it now; `onClose` once it's
// closed, removed or gone.
export function ReminderDialog({
  reminder,
  trip,
  onOpenItem,
  onChanged,
  onClose,
}: {
  reminder: Reminder;
  trip: TripView;
  // What it's about, opened on its card (the dialog closes first).
  onOpenItem: ((id: string) => void) | null;
  onChanged: () => void;
  onClose: () => void;
}) {
  const say = useJustDone();
  const readOnly = useReadOnly();
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<"save" | "remove" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Removed, at this point: closed once the list has it gone (or after a while all the same).
  const close = useRef(onClose);
  close.current = onClose;
  const gone = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(gone.current), []);
  // Back from editing, the focus is on Edit again.
  const root = useRef<HTMLDivElement>(null);
  const [backFrom, setBackFrom] = useState(false);
  useEffect(() => {
    if (!backFrom || editing) return;
    root.current?.querySelector<HTMLElement>(".tm-reminder-edit")?.focus();
    setBackFrom(false);
  }, [backFrom, editing]);

  const backToReading = () => {
    setEditing(false);
    setBackFrom(true);
  };
  const quote = `“${reminder.text.length > 60 ? `${reminder.text.slice(0, 59)}…` : reminder.text}”`;
  const remove = async () => {
    if (busy) return;
    setError(null);
    setBusy("remove");
    try {
      const { change } = await api.removeReminder(trip.id, reminder.id, reminder.version);
      onChanged();
      say({ change, text: `Removed the reminder ${quote}` });
      gone.current = setTimeout(() => close.current(), 5000);
    } catch (e) {
      setBusy(null);
      if (e instanceof ApiError && e.status === 409 && e.data["reminder"]) {
        // Changed meanwhile: shown as it is now, said why it's still there. Sent meanwhile: it leaves the list, and
        // the dialog with it, the trip's notice saying why.
        const fresh = e.data["reminder"] as Reminder;
        onChanged();
        if (fresh.sent_at || fresh.missed_at) return say({ change: null, text: e.message });
      }
      setError(e instanceof Error ? e.message : "Couldn't remove the reminder. Try again.");
    }
  };

  const facts = [
    { key: "when", label: "When", value: localWhen(reminder.at, reminder.tz) },
    { key: "for", label: "For", value: whom(reminder, trip) },
    ...(reminder.about
      ? [
          {
            key: "about",
            label: "About",
            value: onOpenItem ? (
              <button
                type="button"
                className="tm-reminder-about"
                onClick={() => {
                  const about = reminder.about?.id;
                  onClose();
                  if (about) onOpenItem(about);
                }}
              >
                {reminder.about.title || "What it's about"}
              </button>
            ) : (
              reminder.about.title || "Something since removed"
            ),
          },
        ]
      : []),
    {
      key: "set",
      label: "Set by",
      value: `${reminder.created.by.id === trip.you ? "You" : asLabel(reminder.created.by.name)}, ${setOn(reminder.created.at)}`,
    },
  ];

  return (
    <Modal
      title={editing ? "Change the reminder" : "Reminder"}
      busy={busy !== null}
      focus=".tm-reminder-close"
      onCancel={editing ? backToReading : onClose}
      className="tm-reminder-dialog"
    >
      {({ describedBy }) => (
        <div ref={root} className="tm-reminder-body">
          {editing ? (
            <ReminderForm
              reminder={reminder}
              trip={trip}
              offline={offline}
              describedBy={describedBy}
              onChanged={onChanged}
              onDone={backToReading}
            />
          ) : (
            <>
              <div id={describedBy} className="tm-dialog-body">
                <p className="body tm-reminder-text">{reminder.text}</p>
                <dl className="tm-reminder-facts">
                  {facts.map((f) => (
                    <div key={f.key} className="tm-reminder-fact" data-fact={f.key}>
                      <dt className="tm-settings-label">{f.label}</dt>
                      <dd className="callout">{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              {error && (
                <p className="callout tm-error" role="alert">
                  {error}
                  <ReportThis />
                </p>
              )}
              <div className="tm-dialog-actions tm-reminder-actions">
                {!readOnly && (
                  <Button
                    variant="quiet"
                    size="sm"
                    icon="close"
                    className="tm-reminder-remove"
                    disabled={busy !== null}
                    onClick={remove}
                  >
                    {busy === "remove" ? "Removing…" : "Remove"}
                  </Button>
                )}
                <Button variant="secondary" className="tm-reminder-close" disabled={busy !== null} onClick={onClose}>
                  Close
                </Button>
                {!readOnly && (
                  <Button
                    variant="primary"
                    className="tm-reminder-edit"
                    disabled={busy !== null}
                    onClick={() => {
                      setError(null);
                      setEditing(true);
                    }}
                  >
                    Edit
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

const isDate = (d: string) => /^\d{4}-\d{2}-\d{2}$/.test(d);
const isTime = (t: string) => /^\d{2}:\d{2}$/.test(t);
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

// Its words, its day and time in its own time zone, and who it's for: Save and Cancel. Saved, it waits for the page
// to load the change, so the dialog never shows it as it was.
function ReminderForm({
  reminder,
  trip,
  offline,
  describedBy,
  onChanged,
  onDone,
}: {
  reminder: Reminder;
  trip: TripView;
  offline: boolean;
  describedBy: string;
  onChanged: () => void;
  onDone: () => void;
}) {
  const id = useId();
  const say = useJustDone();
  // The reminder as the form read it: its version is what the change is written over.
  const [base, setBase] = useState(reminder);
  const [text, setText] = useState(reminder.text);
  const growText = useGrow(text);
  const [day, setDay] = useState(reminder.at.slice(0, 10));
  const [time, setTime] = useState(reminder.at.slice(11, 16));
  // Who it's for: everyone, or those ticked among the people in the trip now (someone who left isn't offered).
  const inTrip = (to: Reminder["to"]) =>
    to === "everyone" ? [] : to.flatMap((p) => (trip.members.some((m) => m.id === p.id) ? [p.id] : []));
  const [everyone, setEveryone] = useState(reminder.to === "everyone");
  const [picked, setPicked] = useState<string[]>(() => inTrip(reminder.to));
  const [problems, setProblems] = useState<Partial<Record<"text" | "when" | "to", string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [meanwhile, setMeanwhile] = useState(false);
  const [busy, setBusy] = useState(false);
  // Saved, at this version: back to reading it once the page has loaded it (or after a while all the same).
  const [saved, setSaved] = useState<number | null>(null);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (saved === null) return;
    if (reminder.version >= saved) return done.current();
    const timer = setTimeout(() => done.current(), 5000);
    return () => clearTimeout(timer);
  }, [saved, reminder.version]);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    form.current?.querySelector<HTMLElement>("textarea")?.focus();
  }, []);
  // Me first, then the others by name.
  const people = [...trip.members].sort(
    (a, b) => Number(b.id === trip.you) - Number(a.id === trip.you) || a.name.localeCompare(b.name),
  );
  const city = zoneCity(base.tz);

  const save = async () => {
    if (offline || busy || saved !== null) return;
    const words = text.trim();
    const found: typeof problems = {};
    if (!words) found.text = "Say what it reminds of.";
    const at = `${day}T${time}`;
    if (!isDate(day) || !isTime(time)) found.when = "Choose its day and time.";
    else if (zonedToUtc(at, base.tz).getTime() <= Date.now())
      found.when = `That time has already passed in ${city}. Choose one still to come.`;
    if (!everyone && picked.length === 0) found.to = "Choose who it's for.";
    setProblems(found);
    if (Object.keys(found).length > 0) {
      form.current?.querySelector<HTMLElement>("[aria-invalid='true']")?.focus();
      return;
    }
    // Only what changed goes: who it's for, left as it was, keeps anyone on it who has since left the trip.
    const edit = {
      ...(words !== base.text ? { text: words } : {}),
      ...(at !== base.at.slice(0, 16) ? { at } : {}),
      ...(everyone
        ? base.to === "everyone"
          ? {}
          : { to: "everyone" as const }
        : base.to !== "everyone" && sameSet(picked, inTrip(base.to)) && inTrip(base.to).length === base.to.length
          ? {}
          : { to: picked }),
    };
    if (Object.keys(edit).length === 0) return done.current();
    setError(null);
    setMeanwhile(false);
    setBusy(true);
    try {
      const { change, version } = await api.editReminder(trip.id, base.id, { version: base.version, ...edit });
      onChanged();
      if (!change) return done.current();
      say({ change, text: `Changed the reminder “${words.length > 60 ? `${words.slice(0, 59)}…` : words}”` });
      setSaved(version);
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && e.status === 409 && e.data["reminder"]) {
        const fresh = e.data["reminder"] as Reminder;
        onChanged();
        // Sent meanwhile: it leaves the list, and the dialog with it; the trip's notice says why nothing was saved.
        if (fresh.sent_at || fresh.missed_at) {
          say({ change: null, text: e.message });
          return;
        }
        setBase(fresh);
        setMeanwhile(true);
        return;
      }
      if (e instanceof ApiError && e.status === 400 && /passed/.test(e.message)) {
        setProblems({ when: e.message });
        return;
      }
      setError(e instanceof Error ? e.message : "Couldn't save the reminder. Try again.");
    }
  };

  const field = (key: keyof typeof problems, extra = "") =>
    `sv-field tm-booking-field${extra ? ` ${extra}` : ""}${problems[key] ? " is-wrong" : ""}`;
  return (
    <form
      ref={form}
      className="tm-reminder-form"
      aria-label="Change the reminder"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      {meanwhile && (
        <p className="callout tm-booking-note is-meanwhile" role="alert">
          <Icon name="people" size={20} className="tm-booking-icon" />
          <span>It changed while this was open, so nothing was saved. Check it and save again.</span>
        </p>
      )}
      <div className={field("text")}>
        <label className="sv-field-label" htmlFor={`${id}-text`}>
          Message
        </label>
        <textarea
          ref={growText}
          id={`${id}-text`}
          className="sv-field-input tm-reminder-text-input"
          value={text}
          rows={3}
          maxLength={MAX_TEXT}
          aria-invalid={problems.text ? true : undefined}
          onChange={(e) => {
            setText(e.target.value);
            setProblems(({ text: _, ...rest }) => rest);
          }}
        />
        {problems.text && <span className="sv-field-hint tm-error">{problems.text}</span>}
      </div>
      <fieldset className="tm-reminder-when">
        <legend className="sv-field-label">When</legend>
        <div className="tm-reminder-pair">
          <div className={field("when")}>
            <label className="caption tm-muted" htmlFor={`${id}-day`}>
              Day
            </label>
            <input
              id={`${id}-day`}
              className="sv-field-input"
              type="date"
              value={day}
              aria-invalid={problems.when ? true : undefined}
              aria-describedby={`${id}-when-hint`}
              onChange={(e) => {
                setDay(e.target.value);
                setProblems(({ when: _, ...rest }) => rest);
              }}
            />
          </div>
          <div className={field("when")}>
            <label className="caption tm-muted" htmlFor={`${id}-time`}>
              Time
            </label>
            <input
              id={`${id}-time`}
              className="sv-field-input"
              type="time"
              value={time}
              aria-invalid={problems.when ? true : undefined}
              aria-describedby={`${id}-when-hint`}
              onChange={(e) => {
                setTime(e.target.value);
                setProblems(({ when: _, ...rest }) => rest);
              }}
            />
          </div>
        </div>
        <span id={`${id}-when-hint`} className={`sv-field-hint${problems.when ? " tm-error" : ""}`}>
          {problems.when ?? `${city} time, as it was set.`}
        </span>
      </fieldset>
      <fieldset className="tm-reminder-for">
        <legend className="sv-field-label">For</legend>
        <SegmentedControl
          label="Who it's for"
          value={everyone ? "everyone" : "some"}
          onChange={(v) => {
            setEveryone(v === "everyone");
            setProblems(({ to: _, ...rest }) => rest);
          }}
          options={[
            { value: "everyone", label: "Everyone" },
            { value: "some", label: "Some people" },
          ]}
        />
        {!everyone && (
          <ul className="tm-document-people" aria-label="Who it's for">
            {people.map((m) => (
              <li key={m.id}>
                <label className="tm-document-person">
                  <input
                    type="checkbox"
                    checked={picked.includes(m.id)}
                    onChange={(e) => {
                      setPicked((p) => (e.target.checked ? [...p, m.id] : p.filter((x) => x !== m.id)));
                      setProblems(({ to: _, ...rest }) => rest);
                    }}
                  />
                  <span className="callout">{m.id === trip.you ? `You (${m.name})` : m.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {problems.to && <span className="sv-field-hint tm-error">{problems.to}</span>}
      </fieldset>
      <p id={describedBy} className="caption tm-muted">
        Tripmo sends it then, as a notification on their devices and by email.
      </p>
      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: saving needs signal.</span>
        </p>
      )}
      {error && (
        <p className="callout tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      <div className="tm-dialog-actions">
        <Button variant="secondary" type="button" disabled={busy || saved !== null} onClick={onDone}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy || saved !== null || offline}>
          {busy || saved !== null ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}
